/**
 * Base Button Component
 * Works on React Native and Web
 * Supports dynamic theme colors via themeColor prop
 */

import React from 'react';
import { View, TouchableOpacity, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { colors, fontSizes, fontWeights } from '../theme/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  children: string | React.ReactNode;
  onPress: () => void;
  style?: ViewStyle;
  testID?: string;
  themeColor?: string; // Dynamic theme color for primary buttons
}

const getButtonStyle = (variant: ButtonVariant, disabled: boolean, themeColor?: string): ViewStyle => {
  const baseStyle: ViewStyle = {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: disabled ? 0.5 : 1,
  };

  switch (variant) {
    case 'primary':
      return {
        ...baseStyle,
        backgroundColor: themeColor || colors.primary,
      };
    case 'secondary':
      return {
        ...baseStyle,
        backgroundColor: colors.gray1,
      };
    case 'outline':
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: themeColor || colors.primary,
      };
    case 'ghost':
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
      };
    default:
      return baseStyle;
  }
};

const getTextStyle = (variant: ButtonVariant, themeColor?: string): TextStyle => {
  switch (variant) {
    case 'primary':
      return {
        color: colors.white,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.semibold,
      };
    case 'secondary':
      return {
        color: colors.gray9,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.semibold,
      };
    case 'outline':
      return {
        color: themeColor || colors.primary,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.semibold,
      };
    case 'ghost':
      return {
        color: themeColor || colors.primary,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.semibold,
      };
    default:
      return {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.semibold,
      };
  }
};

/**
 * Button Component
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  children,
  onPress,
  style,
  testID,
  themeColor,
}) => {
  const isDisabled = disabled || loading;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isDisabled}
      testID={testID}
    >
      <View style={[getButtonStyle(variant, isDisabled, themeColor), style]}>
        {loading ? (
          <Text style={getTextStyle(variant, themeColor)}>Loading...</Text>
        ) : typeof children === 'string' ? (
          <Text style={getTextStyle(variant, themeColor)}>{children}</Text>
        ) : (
          children
        )}
      </View>
    </TouchableOpacity>
  );
};

